// ============================================================
// A PLATE'S FRAME FOR ITS HITS (plan body-map H2, H3): where on a drawing a
// burst's moments, specials, glances and rattle land - each place's anchor
// and box, the whole drawing's bounds, its scale, and whether it has line
// art to glow. One per drawing (a person's art, a vehicle's, the goo).
//
// Pure. Zero React dependency. Frame units.
// ============================================================

import { partBox } from './figureArt';

export type Point = readonly [number, number];
export type Box = readonly [number, number, number, number];

export interface PlateFrame {
  /** The whole drawing's bounds (where glances pass and the ground lies). */
  box: Box;
  /** Each place's anchor (where a hit there is centred). */
  anchors: Partial<Record<string, Point>>;
  /** A place's box; for what the drawn regions leave (a person's Body, a vehicle's Hull), the art's own box. */
  partBox: (location: string) => Box;
  /** 1 on a figure's frame (2123 x 3000); smaller on the goo's. */
  k: number;
  /** Line art with a clip per part (a person, a vehicle); the goo has neither. */
  lines: boolean;
}

export const middleOf = ({ box }: Pick<PlateFrame, 'box'>): Point => [(box[0] + box[2]) / 2, (box[1] + box[3]) / 2];

/** Where a location is struck: its anchor, else the body's, else the figure's middle. */
export const anchorOf = (f: Pick<PlateFrame, 'anchors' | 'box'>, location: string): Point =>
  f.anchors[location] ?? f.anchors.body ?? middleOf(f);

/** What a drawing with line art gives its frame: its regions, its own box, its anchors. */
interface FramedArt {
  regions: Partial<Record<string, readonly string[]>>;
  box: readonly [number, number, number, number];
  anchors: Partial<Record<string, Point>>;
}

const frames = new WeakMap<FramedArt, PlateFrame>();

/**
 * A line-art drawing's frame, made once per art: its whole bounds by `boundsOf`, and each part's box parsed from its
 * polygons the first time it is asked for. The art is never mutated (CLAUDE.md), so its identity keys the frame.
 */
export function artFrame<A extends FramedArt>(art: A, boundsOf: (art: A) => Box): PlateFrame {
  const known = frames.get(art);
  if (known) return known;
  const boxes = new Map<string, Box>();
  const frame: PlateFrame = {
    box: boundsOf(art),
    anchors: art.anchors,
    partBox: (part) => {
      const made = boxes.get(part) ?? partBox(art.regions, art.box, part);
      boxes.set(part, made);
      return made;
    },
    k: 1,
    lines: true,
  };
  frames.set(art, frame);
  return frame;
}
